<template>
  <SettingsPageLayout>
    <div v-if="loaded" class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      <Card>
        <div class="h-48 flex flex-col justify-between">
          <div>
            <div class="flex justify-between">
              <div>
                <h3 class="text-lg font-medium leading-6 text-gray-900 mr-2">Job Refresh</h3>
              </div>
              <ToggleInput @update:modelValue="updateJobRefresh($event)" :modelValue="team.useJobRefreshWorkflow" />
            </div>
            <p class="mt-1 text-sm text-gray-500">
              When a job description hasn't been updated in at least 30 days, Humaniz can remind you to update it. We
              have found that job descriptions which are updated regularly typically result in 20% more candidates. This
              is due to platforms like Indeed viewing older jobs as stale.
            </p>
          </div>
          <div class="flex justify-between">
            <TButton label="Settings" color="white" @click="showJobRefreshSettings = true" />
            <div class="mt-2">
              <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                Email
              </span>
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <div class="h-48 flex flex-col justify-between">
          <div>
            <div class="flex justify-between">
              <div>
                <h3 class="text-lg font-medium leading-6 text-gray-900 mr-2">New Teammate</h3>
              </div>
              <ToggleInput
                @update:modelValue="updateNewTeammateReferral($event)"
                :modelValue="team.notifyNewTeammateReferral"
              />
            </div>
            <p class="mt-1 text-sm text-gray-500">
              When a new Teammate is added to Humaniz, Humaniz sends them a message from your team with their unique
              referral link so they can refer agents to your team ensuring that everything is tracked in Humaniz.
            </p>
          </div>
          <div class="flex justify-between">
            <TButton label="Settings" color="white" @click="showNewTeammateSettings = true"> </TButton>
            <div class="mt-2">
              <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                Email
              </span>
              <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                SMS
              </span>
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <div class="h-48 flex flex-col justify-between">
          <div>
            <div class="flex justify-between">
              <div>
                <h3 class="text-lg font-medium leading-6 text-gray-900 mr-2">Pipeline Reviews</h3>
              </div>
              <ToggleInput
                @update:modelValue="updatePipelineReview($event)"
                :modelValue="team.sendPipelineReviewReminder"
              />
            </div>
            <p class="mt-1 text-sm text-gray-500">
              On the first of the month, Humaniz sends your team a message to self-schedule their Pipeline Review with
              the User assigned to conduct Pipeline Reviews in Humaniz. Pipeline Reviews are conducted monthly.
            </p>
          </div>
          <div class="flex justify-end">
            <!-- <TButton label="Settings" color="white" @click="showPipelineReviewSettings = true">
            </TButton> -->
            <div class="mt-2">
              <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                Email
              </span>
              <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                SMS
              </span>
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <h3 class="text-lg font-medium leading-6 text-gray-900 mr-2">Need a workflow?</h3>
        <p class="mt-1 text-sm text-gray-500">
          Feel free to email us at support@humaniz.io with any workflow ideas you may have. We will get to building them
          right away!
        </p>
      </Card>
    </div>

    <NewTeammateSettings
      v-if="showNewTeammateSettings"
      @handleCancelSettings="showNewTeammateSettings = false"
      @handleSaveSettings="showNewTeammateSettings = false"
    />
    <PipelineReviewSettings
      v-if="showPipelineReviewSettings"
      @handleCancelSettings="showPipelineReviewSettings = false"
      @handleSaveSettings="showPipelineReviewSettings = false"
    />
    <JobRefreshSettings
      v-if="showJobRefreshSettings"
      @handleCancelSettings="showJobRefreshSettings = false"
      @handleSaveSettings="showJobRefreshSettings = false"
    />
  </SettingsPageLayout>
</template>

<script>
import SettingsPageLayout from '../../SettingsPageLayout/index.vue';
import Card from '../../Card/index.vue';
import NewTeammateSettings from './settings/NewTeammateSettings.vue';
import PipelineReviewSettings from './settings/PipelineReviewSettings.vue';
import JobRefreshSettings from './settings/JobRefreshSettings.vue';

export default {
  components: {
    SettingsPageLayout,
    Card,
    NewTeammateSettings,
    PipelineReviewSettings,
    JobRefreshSettings,
  },
  data() {
    return {
      loaded: false,
      team: {
        id: '',
        notifyNewTeammateReferral: false,
        sendPipelineReviewReminder: false,
        useJobRefreshWorkflow: false,
      },
      showNewTeammateSettings: false,
      showPipelineReviewSettings: false,
      showJobRefreshSettings: false,
    };
  },
  async mounted() {
    await this.getTeam();
  },
  methods: {
    handleCancelSettings() {
      const self = this;
      self.showSettings = false;
    },
    handleSaveSettings() {
      const self = this;
      self.showSettings = false;
    },
    async getTeam() {
      try {
        this.$store.commit('general/showLoadingSpinner');
        const response = await fetch(`/api/teams/1`);
        const data = await response.json();

        this.$store.commit('general/hideLoadingSpinner');
        this.team.id = data.record.id;
        this.team.notifyNewTeammateReferral = data.record.notify_new_teammate_referral;
        this.team.sendPipelineReviewReminder = data.record.send_pipeline_review_reminder;
        this.team.useJobRefreshWorkflow = data.record.use_job_refresh_workflow;
        this.loaded = true;
      } catch (error) {
        this.$store.commit('general/hideLoadingSpinner');
        this.$store.commit('general/showAlert', {
          alertType: 'error',
          alertTitle: 'Error loading team',
          alertDescription: 'An unexpected error occurred',
        });
      }
    },
    async updateNewTeammateReferral(e) {
      try {
        // Update the local state with the new toggle value first
        this.team.notifyNewTeammateReferral = e;

        this.$store.commit('general/showLocalLoadingSpinner');

        // Only proceed with API call if we have a valid team ID
        if (!this.team.id) {
          this.$store.commit('general/hideLocalLoadingSpinner');
          this.$store.commit('general/showAlert', {
            alertType: 'error',
            alertTitle: 'Workflow update error',
            alertDescription: 'Unable to update: Team ID is missing',
          });
          return;
        }

        const response = await fetch(`/api/teams/${this.team.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            notify_new_teammate_referral: e,
          }),
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelectorAll('[name="csrf-token"]')[0].content,
          },
        });

        const data = await response.json();
        this.$store.commit('general/hideLocalLoadingSpinner');

        this.$store.commit('general/showAlert', {
          alertType: data.status !== 'error' ? 'success' : 'error',
          alertTitle: data.status !== 'error' ? 'Workflow updated' : 'Workflow update error',
          alertDescription:
            data.status !== 'error'
              ? 'Your workflow was successfully updated. Use the button below to dismiss this alert.'
              : data.message,
        });
      } catch (error) {
        this.$store.commit('general/hideLocalLoadingSpinner');
        this.$store.commit('general/showAlert', {
          alertType: 'error',
          alertTitle: 'Workflow update error',
          alertDescription: 'An unexpected error occurred',
        });
      }
    },
    async updatePipelineReview(e) {
      try {
        // Update the local state with the new toggle value first
        this.team.sendPipelineReviewReminder = e;

        this.$store.commit('general/showLocalLoadingSpinner');

        // Only proceed with API call if we have a valid team ID
        if (!this.team.id) {
          this.$store.commit('general/hideLocalLoadingSpinner');
          this.$store.commit('general/showAlert', {
            alertType: 'error',
            alertTitle: 'Workflow update error',
            alertDescription: 'Unable to update: Team ID is missing',
          });
          return;
        }

        const response = await fetch(`/api/teams/${this.team.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            send_pipeline_review_reminder: e,
          }),
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelectorAll('[name="csrf-token"]')[0].content,
          },
        });

        const data = await response.json();
        this.$store.commit('general/hideLocalLoadingSpinner');

        this.$store.commit('general/showAlert', {
          alertType: data.status !== 'error' ? 'success' : 'error',
          alertTitle: data.status !== 'error' ? 'Workflow updated' : 'Workflow update error',
          alertDescription:
            data.status !== 'error'
              ? 'Your workflow was successfully updated. Use the button below to dismiss this alert.'
              : data.message,
        });
      } catch (error) {
        this.$store.commit('general/hideLocalLoadingSpinner');
        this.$store.commit('general/showAlert', {
          alertType: 'error',
          alertTitle: 'Workflow update error',
          alertDescription: 'An unexpected error occurred',
        });
      }
    },
    async updateJobRefresh(e) {
      try {
        // Update the local state with the new toggle value first
        this.team.useJobRefreshWorkflow = e;

        this.$store.commit('general/showLocalLoadingSpinner');

        // Only proceed with API call if we have a valid team ID
        if (!this.team.id) {
          this.$store.commit('general/hideLocalLoadingSpinner');
          this.$store.commit('general/showAlert', {
            alertType: 'error',
            alertTitle: 'Workflow update error',
            alertDescription: 'Unable to update: Team ID is missing',
          });
          return;
        }

        const response = await fetch(`/api/teams/${this.team.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            use_job_refresh_workflow: e,
          }),
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelectorAll('[name="csrf-token"]')[0].content,
          },
        });

        const data = await response.json();
        this.$store.commit('general/hideLocalLoadingSpinner');

        this.$store.commit('general/showAlert', {
          alertType: data.status !== 'error' ? 'success' : 'error',
          alertTitle: data.status !== 'error' ? 'Workflow updated' : 'Workflow update error',
          alertDescription:
            data.status !== 'error'
              ? 'Your workflow was successfully updated. Use the button below to dismiss this alert.'
              : data.message,
        });
      } catch (error) {
        this.$store.commit('general/hideLocalLoadingSpinner');
        this.$store.commit('general/showAlert', {
          alertType: 'error',
          alertTitle: 'Workflow update error',
          alertDescription: 'An unexpected error occurred',
        });
      }
    },
  },
};
</script>
